
import React, { createContext, useContext, useState, useEffect } from 'react';

interface NotificationContextType {
  unreadCount: number;
  setUnreadCount: (count: number) => void;
  decrementUnreadCount: () => void;
  incrementUnreadCount: () => void;
  resetUnreadCount: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider = ({ children }: { children: React.ReactNode }) => {
  // Initialize with value from localStorage if available
  const [unreadCount, setUnreadCount] = useState<number>(() => {
    const savedCount = localStorage.getItem('unreadNotificationsCount');
    return savedCount ? parseInt(savedCount, 10) : 0;
  });

  // Update localStorage whenever unreadCount changes
  useEffect(() => {
    localStorage.setItem('unreadNotificationsCount', unreadCount.toString());
  }, [unreadCount]);

  const decrementUnreadCount = () => {
    setUnreadCount((prev) => Math.max(0, prev - 1));
  };

  const incrementUnreadCount = () => {
    setUnreadCount((prev) => prev + 1);
  };

  const resetUnreadCount = () => {
    setUnreadCount(0);
  };

  return (
    <NotificationContext.Provider
      value={{
        unreadCount,
        setUnreadCount,
        decrementUnreadCount,
        incrementUnreadCount,
        resetUnreadCount
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
